import AuthService from './AuthService';

class AgentService {
    constructor() {
        const rawBaseUrl = process.env.REACT_APP_CHATBOT_API_URL || process.env.CHATBOT_API_URL || process.env.REACT_APP_API_URL || '';
        this.apiBaseUrl = String(rawBaseUrl).replace(/\/+$/, '');
        this.agentsPath = '/private/agents';
    }

    getAuthHeaders(extraHeaders) {
        const token = AuthService.getAccessToken();
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
        return {
            ...authHeader,
            ...(extraHeaders || {}),
        };
    }

    sanitizeCreatePayload(payload) {
        const body = { ...(payload || {}) };
        // Backend-managed fields.
        delete body.agentId;
        delete body.createdAt;
        return body;
    }

    sanitizeUpdatePayload(agentId, payload) {
        const body = { ...(payload || {}) };
        body.agentId = agentId;
        // Backend-managed fields.
        delete body.createdAt;
        return body;
    }

    async getAgents() {
        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${loginAs}` : '';
        const url = `${this.apiBaseUrl}${this.agentsPath}${loginAsParam}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async getAgent(agentId) {
        if (!agentId) throw new Error('agentId is required');

        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${loginAs}` : '';
        const url = `${this.apiBaseUrl}${this.agentsPath}/${encodeURIComponent(agentId)}${loginAsParam}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async createAgent(payload) {
        const url = `${this.apiBaseUrl}${this.agentsPath}`;
        const body = this.sanitizeCreatePayload(payload);

        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async updateAgent(agentId, payload) {
        if (!agentId) throw new Error('agentId is required');

        const url = `${this.apiBaseUrl}${this.agentsPath}/${encodeURIComponent(agentId)}`;
        const body = this.sanitizeUpdatePayload(agentId, payload);

        const response = await fetch(url, {
            method: 'PUT',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async discoverMcpTools(agentId, serverId) {
        const loginAs = AuthService.getLoginAs();
        const loginAsParam = loginAs ? `?loginAs=${encodeURIComponent(loginAs)}` : '';
        const url = `${this.apiBaseUrl}${this.agentsPath}/${encodeURIComponent(agentId)}/mcp/servers/${encodeURIComponent(serverId)}/tools${loginAsParam}`;
        const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        return data && typeof data === 'object' ? data : { tools: [], detectedTransport: null };
    }

    async deleteMcpServer(agentId, serverId) {
        const url = `${this.apiBaseUrl}${this.agentsPath}/${encodeURIComponent(agentId)}/mcp/servers/${encodeURIComponent(serverId)}`;
        const response = await fetch(url, { method: 'DELETE', headers: this.getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    }

    async deleteAgent(agentId) {
        if (!agentId) throw new Error('agentId is required');

        const url = `${this.apiBaseUrl}${this.agentsPath}/${encodeURIComponent(agentId)}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }
}

const agentService = new AgentService();
export { AgentService };
export default agentService;
